import React, { useCallback } from 'react';
import { GetMeQueryQueryResult, UpdateMeMutationResult, User, useUpdateMeMutation, useGetMeQueryQuery, useRemoveOtherUserProxyMutation } from '../../generated/graphql';
import { useDiaEditor } from "../../components/DiaEditor";
import WaitSpinner from '../../components/wait-spinner';
import { NonIdealState } from '../../components/NonIdealState';
import { Accordion, AccordionSummary, AccordionDetails, TextField, IconButton, Alert, Button, ListItem, ListItemIcon } from '@mui/material';
import { VisibilityOff, Visibility, Save, Cancel } from '@mui/icons-material';
import { UserSelect } from '../../components/MaterialSelects/UserSelect';
import { useSnackbar } from "notistack";
import { Delete } from '@mui/icons-material';
import { ModalWindow } from '../../components/ModalWindow';
import { Typography } from '@mui/material';
import { useDaybookContext } from '../../contexts/Daybook/DayBookContext';

function blankValues(): User {
    return {
        id: '',
        proxyUser: null,
        password: '',
        usersIProxy: []
    };
};

const relationships = {
    proxyUser: 'connect',
    usersIproxy: 'connect'
};

export const Me = () => {
    const query = useGetMeQueryQuery();
    const { enqueueSnackbar } = useSnackbar();
    const [saveMutation, saveResult] = useUpdateMeMutation();
    const [removeProxyMutation, removeProxyResult] = useRemoveOtherUserProxyMutation();
    const [showPassword, setShowPassword] = React.useState(false);
    const [selectedProxy, setSelectedProxy] = React.useState(0);
    const daybook = useDaybookContext();
    const save = useCallback(({ values }) => {
        return saveMutation({ variables: { input: values } });
    }, []);
    const getValue = useCallback((result: GetMeQueryQueryResult) => {
        return result?.data?.me;
    }, []);
    const getSaveValue = useCallback((result: UpdateMeMutationResult) => {
        return result?.data?.updateMe;
    }, []);


    const doDeleteUserProxy = async () => {
        await removeProxyMutation({ variables: { input: { ids: [selectedProxy] } } });
        setSelectedProxy(0);
        enqueueSnackbar('User proxy removed', { variant: 'success' });
    };
    const data = useDiaEditor<User, GetMeQueryQueryResult, null, UpdateMeMutationResult>({
        save, saveResult, query: query,
        baseUrl: '/me',
        blankValues: blankValues,
        label: 'Options',
        permission: 'options',
        getValue: getValue,
        allowRedirectWhen: () => true
    });
    const values = data.form.values;

    if (query.error) {
        return <NonIdealState
            severity="error"
            title="An error has occurred"
            message="GraphQL error encountered"
        />;
    }
    if (data.form.status == 'loading') {
        return <WaitSpinner variant="panel" />;
    }
    const field = {
        onChange: data.form?.onChange
    };
    const handleClickShowPassword = () => {
        setShowPassword((v) => !v);
    }
    const doProxySave = async () => {
        const update = {};
        if (values.proxyUser) {
            update.connect = values.proxyUser.id;
        } else {
            update.disconnect = true;
        }
        await saveMutation({
            variables: {
                input: {
                    proxyUser: update
                }
            }
        });
        enqueueSnackbar('User Proxy updated', { variant: 'success' });
    };
    const doPasswordSave = async () => {
        const pw = values.password?.replace(' ', '');
        if (pw != values.password2) {
            enqueueSnackbar('Passwords do not match', { variant: 'error' });
            return;
        }
        if (!pw || pw.length < 8) {
            enqueueSnackbar('Password must be 8 characters or more', { variant: 'error' });
            return;
        }
        await saveMutation({
            variables: {
                input: {
                    password: values.password
                }
            }
        });
        data.form.setValue('password', '');
        data.form.setValue('password2', '');
        enqueueSnackbar('Password updated', { variant: 'success' });
    }
    const doProxyDelete = (id) => {

        return '';
    }
    return <div className="is-scrollable"><Accordion defaultExpanded={true}>
        <AccordionSummary>My Account</AccordionSummary>
        <AccordionDetails className="grid12">
            <TextField className="grid12-6" {...field} name="name" value={values?.name} label="Username" disabled={true} />
            <TextField className="grid12-6" {...field} name="email" value={values?.email} label="Email" disabled={true} />
        </AccordionDetails>
    </Accordion>
        <Accordion defaultExpanded={false}>
            <AccordionSummary>Change Password</AccordionSummary>
            <AccordionDetails className="grid12">
                <Alert severity="info">Changes to password will affect your next login.</Alert>
                <TextField label="New Password" {...field}
                    type={showPassword ? 'text' : 'password'}
                    value={values?.password ?? ''}
                    name="password"
                    className="grid12-8 grid12-clear"
                    InputProps={{
                        endAdornment:
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                            >{showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                    }}
                />
                <TextField label="Repeat New Password" {...field}
                    type={showPassword ? 'text' : 'password'}
                    value={values?.password2 ?? ''}
                    name="password2"
                    className="grid12-8 grid12-clear"
                    InputProps={{
                        endAdornment:
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                            >{showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                    }}
                />
                <div><Button onClick={doPasswordSave} variant="contained" color="primary"><Save /> Save</Button></div>
            </AccordionDetails>
        </Accordion>
        {daybook?.challengeEnabled &&
            <>
                <Accordion>
                    <AccordionSummary>Daybook User Proxy</AccordionSummary>
                    <AccordionDetails className="grid12">
                        <Alert severity="info">Assign a user to respond to any new challenge requests for your campaigns.  Note that this will not forward requests that someone else has forwarded to you!</Alert>
                        <UserSelect className="grid12-4" {...field} name="proxyUser" value={values.proxyUser} allowBlank={true} />
                        <div><Button disabled={values?.proxyUser?.id == data?.form?.initialValues?.proxyUser?.id} onClick={doProxySave} variant="contained" color="primary"><Save /> Save</Button></div>
                    </AccordionDetails>
                </Accordion>
                {values?.usersIProxy?.length > 0 &&
                    <Accordion>
                        <AccordionSummary>Daybook Inbound Proxies</AccordionSummary>
                        <AccordionDetails className="grid12">
                            <Alert severity="info">The following users are forwarding any requests to your account.  You may cancel the proxy below</Alert>
                            {
                                values?.usersIProxy?.map((v, idx) => {
                                    return (<ListItem onClick={() => setSelectedProxy(v.id)} key={idx}><ListItemIcon >
                                        <Delete />
                                    </ListItemIcon>{v.name}</ListItem>);
                                })
                            }
                        </AccordionDetails>
                    </Accordion>
                }
                <ModalWindow isOpen={selectedProxy > 0} onClose={() => { }} maxWidth="md" header={
                    <h3><Delete /> Delete User Proxy</h3>
                } actions={
                    <>
                        <Button size="large" color="warning" onClick={doDeleteUserProxy} startIcon={<Delete />}>Delete this proxy</Button>
                        <Button size="large" color="primary" onClick={() => { setSelectedProxy(0) }} startIcon={<Cancel />}>Cancel</Button>
                    </>
                }>
                    <Typography variant="body1">
                        <p>By deleting the proxy you will no longer receive requests for this user.</p>
                        <p>Note this cannot be undone without contacting an Admin</p>
                    </Typography>
                </ModalWindow>
            </>
        }
    </div>;
}
